import { DateField, FunctionField, ListContextProvider, useList, useRecordContext, useGetList } from "react-admin";
import { Typography } from "@mui/material";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { StyledDatagrid } from "@/components/ui/react-admin/StyledDatagrid";
import { Policy, ResourceName } from "@/types";

/**
 * Displays a datagrid of all policies associated with a client.
 * Must be used within a RecordContext containing a client record.
 */
export const ClientPoliciesDatagrid = () => {
  const record = useRecordContext();

  const { data: policies = [], isLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: record?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    {
      enabled: !!record?.id,
    }
  );

  const listContext = useList({
    data: policies,
    isLoading,
    sort: { field: "effective_start_date", order: "DESC" },
  });

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading policies...
      </Typography>
    );
  }

  if (!policies.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No policies found.
      </Typography>
    );
  }

  return (
    <ListContextProvider value={listContext}>
      <StyledDatagrid
        bulkActionButtons={false}
        rowClick={(_id, _resource, row) => `/policies/${row.id}/show`}
        size="small"
        sx={{ "& .RaDatagrid-headerCell": { whiteSpace: "nowrap" } }}
      >
        <FunctionField
          source="vendor_company_name"
          label="Carrier"
          render={(record: Policy) => (record as Record<string, unknown>).vendor_company_name || "No Carrier"}
        />
        <StatusChipField source="status" />
        <DateField source="effective_start_date" label="Start" />
        <DateField source="effective_end_date" label="End" />
      </StyledDatagrid>
    </ListContextProvider>
  );
};
